import {
  IExplainerVideoStep,
  ISelectDumbbellWeight,
  ISelectWeight,
  IWizardStep,
  StrengthAssessmentStep,
  StrengthAssessmentSteps,
  UserProfile,
} from 'fitbeat.gym-core';
import React from 'react';
import { Equipment, Gender } from '../../../models/src';
import { CompleteAssessment } from './CompleteAssessment';
import ExplainAssessment from './ExplainAssessment';
import { ExplainExercise } from './ExplainExercise';
import { GeneralStrengthLevelQuesiton } from './GeneralStrengthLevelQuesiton';
import { ManualSelection } from './ManualSelection';
import { SelectBandWeight } from './SelectBandWeight';
import { SelectDumbbellWeight } from './SelectDumbbellWeight';
import SetupHomeGym from './SetupHomeGym';
import StartAssessment from './StartAssessment';
import StartDumbbellAssessment from './StartDumbbellAssessment';
import { StrengthAssessmentContainer } from './StrengthAssessmentContainer';
import { StrengthSettings } from './StrengthSettings';

export const getStrengthAssessmentRouteConfig = (
  t: any,
  steps: StrengthAssessmentSteps,
  userProfile: UserProfile,
): any[] =>
  steps.map((step: StrengthAssessmentStep) => ({
    route: step.route,
    ScreenComponent: ({ getNextStep, getPreviousStep, navigation }) => (
      <>
        {getScreen(step, userProfile)(
          getNextStep,
          getPreviousStep,
          t,
          navigation,
        )}
      </>
    ),
  }));

const getScreen = (step: StrengthAssessmentStep, userProfile: UserProfile) => {
  const {
    buttonKey, descriptionKey, titleKey, type,
  } = step.config;
  switch (type) {
    case 'GeneralStrengthLevel': {
      const genderReferredToInTitle = userProfile.gender !== Gender.Other ? userProfile.gender : '';
      return (getNextStep, getPreviousStep, t) => (
        <StrengthAssessmentContainer
          currentStep={step}
          getNextStep={getNextStep}
          getPreviousStep={getPreviousStep}
          buttonText={t(buttonKey)}
          title={t(titleKey, { gender: genderReferredToInTitle })}
          description={t(descriptionKey)}
        >
          <GeneralStrengthLevelQuesiton
            getNextStep={getNextStep}
            t={t}
            userProfile={userProfile}
          />
        </StrengthAssessmentContainer>
      );
    }
    case 'StrengthSettings': {
      return (getNextStep, getPreviousStep, t) => (
        <StrengthAssessmentContainer
          currentStep={step}
          getNextStep={getNextStep}
          getPreviousStep={getPreviousStep}
          buttonText={t(buttonKey)}
          title={t(titleKey)}
          description={t(descriptionKey)}
        >
          <StrengthSettings
            getNextStep={getNextStep}
            userProfile={userProfile}
          />
        </StrengthAssessmentContainer>
      );
    }
    case 'StartAssessmentElite': {
      return (getNextStep, getPreviousStep, t, navigation) => (
        <StrengthAssessmentContainer
          currentStep={step}
          getNextStep={getNextStep}
          getPreviousStep={getPreviousStep}
          buttonText={t(buttonKey)}
          title={t(titleKey)}
          description=''
        >
          <StartAssessment t={t} navigation={navigation} />
        </StrengthAssessmentContainer>
      );
    }
    case 'StartDumbbellAssessment': {
      return (getNextStep, getPreviousStep, t, navigation) => (
        <StrengthAssessmentContainer
          currentStep={step}
          getNextStep={getNextStep}
          getPreviousStep={getPreviousStep}
          buttonText={t(buttonKey)}
          title={t(titleKey)}
          description=''
        >
          <StartDumbbellAssessment t={t} />
        </StrengthAssessmentContainer>
      );
    }
    case 'ExplainAssessment': {
      return (getNextStep, getPreviousStep, t) => (
        <StrengthAssessmentContainer
          currentStep={step}
          getNextStep={getNextStep}
          getPreviousStep={getPreviousStep}
          buttonText={t(buttonKey)}
          title={t(titleKey)}
          description=''
        >
          <ExplainAssessment t={t} equipment={Equipment.UniversalBands} />
        </StrengthAssessmentContainer>
      );
    }
    case 'ExplainDumbbellAssessment': {
      return (getNextStep, getPreviousStep, t) => (
        <StrengthAssessmentContainer
          currentStep={step}
          getNextStep={getNextStep}
          getPreviousStep={getPreviousStep}
          buttonText={t(buttonKey)}
          title={t(titleKey)}
          description=''
        >
          <ExplainAssessment t={t} equipment={Equipment.Dumbbells} />
        </StrengthAssessmentContainer>
      );
    }
    case 'ExplainerVideo': {
      const config = step.config as IExplainerVideoStep;
      return (getNextStep, getPreviousStep, t, navigation) => (
        <StrengthAssessmentContainer
          currentStep={step}
          getNextStep={getNextStep}
          getPreviousStep={getPreviousStep}
          buttonText={t(buttonKey, { x: config.numberOfReps })}
          title={config.primaryMuscleMover}
          description={t(descriptionKey, { x: config.numberOfReps })}
        >
          <ExplainExercise
            navigation={navigation}
            videoName={config.videoName}
            recommendation={config.recommendation}
          />
        </StrengthAssessmentContainer>
      );
    }
    case 'SelectWeight': {
      const currentStep = step as IWizardStep<ISelectWeight>;
      return (getNextStep, getPreviousStep, t) => (
        <StrengthAssessmentContainer
          getNextStep={getNextStep}
          currentStep={step}
          getPreviousStep={getPreviousStep}
          buttonText={t(buttonKey)}
          title={currentStep.config.primaryMuscleMover}
          description={t(descriptionKey)}
        >
          <SelectBandWeight currentStep={currentStep} />
        </StrengthAssessmentContainer>
      );
    }
    case 'SelectDumbbellWeight': {
      const currentStep = step as IWizardStep<ISelectDumbbellWeight>;
      return (getNextStep, getPreviousStep, t) => (
        <StrengthAssessmentContainer
          getNextStep={getNextStep}
          currentStep={step}
          getPreviousStep={getPreviousStep}
          buttonText={t(buttonKey)}
          title={currentStep.config.primaryMuscleMover}
          description={t(descriptionKey)}
        >
          <SelectDumbbellWeight currentStep={currentStep} />
        </StrengthAssessmentContainer>
      );
    }
    case 'ManualSelection': {
      return (getNextStep, getPreviousStep, t) => (
        <StrengthAssessmentContainer
          getNextStep={getNextStep}
          title={t(titleKey)}
          description={t(descriptionKey)}
          currentStep={step}
          getPreviousStep={getPreviousStep}
          buttonText={t(buttonKey)}
        >
          <ManualSelection
            getPreviousStep={getPreviousStep}
            userProfile={userProfile}
          />
        </StrengthAssessmentContainer>
      );
    }
    case 'CompleteAssessment': {
      return (getNextStep, getPreviousStep, t) => (
        <StrengthAssessmentContainer
          getNextStep={getNextStep}
          title={t(titleKey)}
          description={t(descriptionKey)}
          currentStep={step}
          getPreviousStep={getPreviousStep}
          buttonText={t(buttonKey)}
        >
          <CompleteAssessment
            getNextStep={getNextStep}
            userProfile={userProfile}
          />
        </StrengthAssessmentContainer>
      );
    }
    case 'SetUpHomeGym': {
      return (getNextStep) => (
        <SetupHomeGym
          getNextStep={getNextStep}
        />
      );
    }
    default: {
      return () => <></>;
    }
  }
};
